import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/layout/Layout';
import { Users, Award, Heart, Globe, Sparkles, Loader } from 'lucide-react';

const TEAM_IMAGE_FALLBACK = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=85&w=2400";
const BEATRIZ_IMAGE = `${process.env.REACT_APP_BACKEND_URL}/uploads/Beatriz%20Aranguez.jpg`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATS = [
  { icon: Users, value: "10,000+", label: "Viajeros satisfechos" },
  { icon: Globe, value: "120+", label: "Destinos" },
  { icon: Award, value: "15", label: "Años de experiencia" },
  { icon: Heart, value: "98%", label: "Clientes recurrentes" }
];

export default function SobreNosotros() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API}/pages/sobre-nosotros`);
        setContent(res.data);
      } catch (err) {
        console.error('Error loading about us content:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader className="w-10 h-10 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img
          src={content?.hero_image || TEAM_IMAGE_FALLBACK}
          alt={content?.title || "Sobre Nosotros"}
          className="w-full h-full object-cover"
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
          <div className="container-custom">
            <h1 className="text-4xl lg:text-6xl font-serif text-white mb-4">
              {content?.title || "Sobre Nosotros"}
            </h1>
            <p className="text-xl text-white/80 max-w-2xl">
              {content?.subtitle || "Conoce al equipo que hace posible tus viajes de ensueño"}
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <section className="py-12 bg-primary">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="w-8 h-8 text-accent mx-auto mb-3" />
                <div className="text-3xl font-serif font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-white/70 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dynamic Sections */}
      {content?.sections && content.sections.length > 0 ? (
        content.sections.map((section, idx) => (
          <section key={idx} className={`section ${idx % 2 === 1 ? 'bg-slate-50' : ''}`}>
            <div className="container-custom">
              <div className={`grid lg:grid-cols-2 gap-16 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={idx % 2 === 1 ? 'order-2 lg:order-1' : ''}>
                  <h2 className="section-title mb-6">{section.title}</h2>
                  <div className="space-y-4 text-muted-foreground whitespace-pre-wrap">
                    {section.content}
                  </div>
                </div>
                <div className={`relative ${idx % 2 === 1 ? 'order-1 lg:order-2' : ''}`}>
                  {section.image ? (
                    <img
                      src={section.image}
                      alt={section.title}
                      className="rounded-2xl shadow-2xl w-full aspect-[4/3] object-cover"
                    />
                  ) : (
                    <div className="aspect-[4/3] bg-slate-200 rounded-2xl flex items-center justify-center">
                      <Sparkles className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  {idx === 0 && (
                    <div className="absolute -bottom-8 -left-8 bg-accent text-white p-6 rounded-xl shadow-xl hidden md:block">
                      <div className="text-4xl font-serif font-bold">15+</div>
                      <div className="text-sm">Años creando sueños</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        ))
      ) : (
        <section className="section">
          <div className="container-custom text-center text-muted-foreground">
            No hay secciones definidas para esta página.
          </div>
        </section>
      )}

      {/* Directora Section (Keep static as it is special/brand identity) */}
      <section className="section bg-white border-y border-slate-100">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-5 gap-12 items-center">
              <div className="md:col-span-2">
                <div className="relative group">
                  <div className="absolute -inset-4 bg-accent/20 rounded-2xl blur-2xl group-hover:bg-accent/30 transition-all duration-500 opacity-70"></div>
                  <img
                    src={BEATRIZ_IMAGE}
                    alt="Beatriz Aranguez - Directora"
                    className="relative rounded-2xl shadow-2xl w-full aspect-[4/5] object-cover"
                  />
                  <div className="absolute -bottom-4 -right-4 bg-primary text-white p-4 rounded-xl shadow-xl">
                    <Sparkles className="w-6 h-6 text-accent" />
                  </div>
                </div>
              </div>
              <div className="md:col-span-3 space-y-6">
                <div>
                  <h2 className="text-3xl font-serif text-primary mb-1">Beatriz Aranguez</h2>
                  <p className="text-accent font-bold uppercase tracking-widest text-sm italic">Directora Fundadora</p>
                </div>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed italic">
                  <p>
                    "Mi pasión por el mundo no comenzó en una oficina, sino en cada rincón que he tenido la suerte de recorrer en los últimos 20 años. Viajes de Ensueño nació de esa necesidad de compartir el mundo, no como un destino turístico, sino como una experiencia transformadora."
                  </p>
                  <p>
                    "Para mí, cada cliente es único. No diseño itinerarios, diseño momentos que se quedan grabados para siempre. Mi compromiso es la cercanía, la transparencia y la excelencia; estar ahí en cada paso para que tu única preocupación sea disfrutar."
                  </p>
                </div>
                <div className="pt-4 flex items-center gap-6">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Heart key={s} className="w-5 h-5 text-accent fill-accent" />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-primary/60 tracking-tight">Pasión y Compromiso Real</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="section-title">Nuestros Valores</h2>
            <p className="section-subtitle mx-auto">
              Los principios que guían cada viaje que diseñamos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-xl bg-secondary/10 flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-primary mb-3">
                Pasión por Viajar
              </h3>
              <p className="text-muted-foreground">
                Amamos lo que hacemos y eso se refleja en cada detalle de los viajes que diseñamos.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-xl bg-secondary/10 flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-primary mb-3">
                Cercanía
              </h3>
              <p className="text-muted-foreground">
                Tratamos a cada cliente como parte de nuestra familia, con atención personalizada y cercana.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-xl bg-secondary/10 flex items-center justify-center mb-6">
                <Award className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-primary mb-3">
                Excelencia
              </h3>
              <p className="text-muted-foreground">
                Nos esforzamos por superar las expectativas en cada viaje que organizamos.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="h-32 bg-slate-50" />
    </Layout>
  );
}
